import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Package, 
  ArrowUpDown, 
  Truck,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  MapPin,
  User
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface InventoryMovement {
  id: string;
  type: 'warehouse_load' | 'transfer_in' | 'transfer_out';
  description: string;
  quantity: number;
  product_name: string;
  from_location: string;
  to_location: string;
  date: string;
  status: 'pending' | 'in_transit' | 'completed';
  responsible_user: string;
}

interface PendingTransfer {
  id: string;
  code: string;
  source_branch: string;
  target_branch: string;
  total_items: number;
  status: string;
  created_at: string;
  requested_by: string;
}

interface InventoryMovementsPanelProps {
  branchId?: string;
}

export function InventoryMovementsPanel({ branchId }: InventoryMovementsPanelProps) {
  const { selectedCompany } = useAuth();
  const navigate = useNavigate();
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [pendingTransfers, setPendingTransfers] = useState<PendingTransfer[]>([]);
  const [loading, setLoading] = useState(true);

  const loadMovementsData = async () => {
    if (!selectedCompany?.id) return;

    try {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      // Get recent inventory movements
      const { data: movementsData, error: movementsError } = await supabase
        .from('inventory_movements')
        .select(`
          id,
          movement_type,
          quantity,
          reference,
          notes,
          created_at,
          user_id,
          branch_id,
          products(name),
          branches(name)
        `)
        .eq('company_id', selectedCompany.id)
        .gte('created_at', yesterday.toISOString())
        .order('created_at', { ascending: false })
        .limit(10);

      if (movementsError) throw movementsError;

      // Get pending transfers with branch names
      const { data: transfersData, error: transfersError } = await supabase
        .from('branch_transfers')
        .select(`
          id,
          code,
          status,
          created_at,
          created_by,
          source_branch_id,
          target_branch_id,
          branch_transfer_items(quantity_requested)
        `)
        .eq('company_id', selectedCompany.id)
        .in('status', ['pending', 'approved', 'sent'])
        .order('created_at', { ascending: false })
        .limit(8);

      if (transfersError) throw transfersError;

      // Transform movements data
      const formattedMovements: InventoryMovement[] = (movementsData || []).map(movement => ({
        id: movement.id,
        type: getMovementType(movement.movement_type),
        description: getMovementDescription(movement.movement_type, movement.reference, movement.notes),
        quantity: movement.quantity,
        product_name: movement.products?.name || 'Producto desconocido',
        from_location: movement.movement_type.includes('in') ? 'Almacén' : movement.branches?.name || 'Tienda',
        to_location: movement.movement_type.includes('out') ? 'Almacén' : movement.branches?.name || 'Tienda',
        date: movement.created_at,
        status: getMovementStatus(movement.movement_type),
        responsible_user: 'Usuario'
      }));

      // Get branch names separately
      const branchIds = [...new Set([
        ...transfersData?.map(t => t.source_branch_id) || [],
        ...transfersData?.map(t => t.target_branch_id) || []
      ].filter(Boolean))];

      const { data: branchesData } = await supabase
        .from('branches')
        .select('id, name')
        .in('id', branchIds);

      const branchMap = new Map(branchesData?.map(branch => [branch.id, branch.name]) || []);

      // Transform transfers data
      const formattedTransfers: PendingTransfer[] = (transfersData || []).map(transfer => ({
        id: transfer.id,
        code: transfer.code,
        source_branch: branchMap.get(transfer.source_branch_id) || 'Origen',
        target_branch: branchMap.get(transfer.target_branch_id) || 'Destino',
        total_items: transfer.branch_transfer_items?.reduce((sum, item) => sum + item.quantity_requested, 0) || 0,
        status: transfer.status,
        created_at: transfer.created_at,
        requested_by: 'Usuario'
      }));

      setMovements(formattedMovements);
      setPendingTransfers(formattedTransfers);

    } catch (error) {
      console.error('Error loading movements data:', error);
      // Use demo data
      setMovements([
        {
          id: '1',
          type: 'warehouse_load',
          description: 'Carga desde almacén central',
          quantity: 50,
          product_name: 'Producto ABC-123',
          from_location: 'Almacén Central',
          to_location: 'Tienda Norte',
          date: new Date().toISOString(),
          status: 'completed',
          responsible_user: 'Juan Pérez'
        },
        {
          id: '2',
          type: 'transfer_in',
          description: 'Traslado recibido',
          quantity: 25,
          product_name: 'Producto XYZ-456',
          from_location: 'Tienda Sur',
          to_location: 'Tienda Norte',
          date: new Date().toISOString(),
          status: 'pending',
          responsible_user: 'María García'
        }
      ]);

      setPendingTransfers([
        {
          id: '1',
          code: 'TRF-001',
          source_branch: 'Tienda Centro',
          target_branch: 'Tienda Norte',
          total_items: 15,
          status: 'pending',
          created_at: new Date().toISOString(),
          requested_by: 'Ana López'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMovementsData();
    // Refresh every 10 minutes
    const interval = setInterval(loadMovementsData, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedCompany?.id, branchId]);

  const getMovementType = (movementType: string): InventoryMovement['type'] => {
    if (movementType.includes('warehouse') || movementType.includes('load')) return 'warehouse_load';
    if (movementType.includes('transfer_in') || movementType.includes('received')) return 'transfer_in';
    return 'transfer_out';
  };

  const getMovementDescription = (type: string, reference?: string, notes?: string) => {
    if (notes) return notes;
    if (reference) return `Movimiento: ${reference}`;
    
    switch (type) {
      case 'warehouse_load':
        return 'Carga desde almacén';
      case 'transfer_in':
        return 'Traslado recibido';
      case 'transfer_out':
        return 'Traslado enviado';
      default:
        return 'Movimiento de inventario';
    }
  };

  const getMovementStatus = (type: string): InventoryMovement['status'] => {
    if (type.includes('pending')) return 'pending';
    if (type.includes('transit')) return 'in_transit';
    return 'completed';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-warning" />;
      case 'approved':
      case 'sent':
      case 'in_transit':
        return <Truck className="h-4 w-4 text-status-info" />;
      case 'completed':
      case 'received':
        return <CheckCircle className="h-4 w-4 text-status-success" />;
      default:
        return <AlertCircle className="h-4 w-4 text-status-error" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-status-warning-bg text-warning border-status-warning-border';
      case 'approved':
      case 'sent':
      case 'in_transit':
        return 'bg-status-info-bg text-status-info border-status-info-border';
      case 'completed':
      case 'received':
        return 'bg-status-success-bg text-status-success border-status-success-border';
      default:
        return 'bg-status-error-bg text-status-error border-status-error-border';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Hace menos de 1h';
    if (diffInHours < 24) return `Hace ${diffInHours}h`;
    return date.toLocaleDateString('es-GT');
  };

  if (loading) {
    return (
      <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
        <CardHeader className="pb-4">
          <div className="animate-pulse">
            <div className="h-6 bg-muted/50 rounded w-48 mb-2"></div>
            <div className="h-4 bg-muted/50 rounded w-32"></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted/50 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center text-lg font-semibold">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center mr-3">
            <Package className="h-4 w-4 text-primary" />
          </div>
          Movimientos de Inventario
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground font-medium">
          Cargas de almacén y traslados pendientes
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Pending Transfers */}
        {pendingTransfers.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-foreground">Traslados Pendientes</h4>
              <Badge variant="outline" className="text-xs">
                {pendingTransfers.length} pendientes
              </Badge>
            </div>
            
            {pendingTransfers.map(transfer => (
              <div 
                key={transfer.id}
                className="bg-background/60 backdrop-blur-sm rounded-xl p-3 border border-border/20 hover:bg-background/80 transition-all duration-200"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(transfer.status)}
                    <span className="font-medium text-sm text-foreground">
                      {transfer.code}
                    </span>
                  </div>
                  <Badge className={`text-xs ${getStatusColor(transfer.status)}`}>
                    {transfer.status === 'pending' ? 'Pendiente' : 
                     transfer.status === 'approved' ? 'Aprobado' :
                     transfer.status === 'sent' ? 'Enviado' : transfer.status}
                  </Badge>
                </div>
                
                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <MapPin className="h-3 w-3" />
                    <span>{transfer.source_branch} → {transfer.target_branch}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Package className="h-3 w-3" />
                    <span>{transfer.total_items} productos</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span>{formatTime(transfer.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Recent Movements */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground">Movimientos Recientes</h4>
          
          {movements.length > 0 ? (
            movements.slice(0, 4).map(movement => (
              <div 
                key={movement.id}
                className="bg-background/60 backdrop-blur-sm rounded-xl p-3 border border-border/20"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <ArrowUpDown className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm text-foreground">
                      {movement.product_name}
                    </span>
                  </div>
                  <Badge className={`text-xs ${getStatusColor(movement.status)}`}>
                    {movement.status === 'pending' ? 'Pendiente' :
                     movement.status === 'in_transit' ? 'En tránsito' :
                     movement.status === 'completed' ? 'Completado' : movement.status}
                  </Badge>
                </div>
                
                <p className="text-xs text-muted-foreground mb-2">
                  {movement.description}
                </p>
                
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center space-x-4">
                    <span className="font-medium">Cantidad: {movement.quantity}</span>
                    <span>{movement.from_location} → {movement.to_location}</span>
                  </div>
                  <span>{formatTime(movement.date)}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-muted-foreground py-6">
              <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No hay movimientos recientes</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2 pt-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/inventory/transfers')}
            className="flex-1 text-xs apple-button"
          >
            <Truck className="h-3 w-3 mr-1" />
            Ver Traslados
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/inventory/movements')}
            className="flex-1 text-xs apple-button"
          >
            <ArrowUpDown className="h-3 w-3 mr-1" />
            Historial
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}